// src/pages/client/DonationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Heart,
  Code,
  Users,
  Wrench,
  Phone,
  Clock,
  Building2,
  User,
  Mail,
  MessageCircle,
  Check,
  X,
  Eye,
  EyeOff,
  Target,
  DollarSign,
  Calendar,
  Loader2,
  HandHeart,
  ArrowLeft
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';

// Redux imports
import {
  createDonationWithPayment,
  getPublicDonations,
  getFeaturedDonations,
  selectPublicDonations,
  selectFeaturedDonations,
  selectDonationsLoading,
  selectDonationSubmitting,
  selectSubmitDonationError,
  selectSubmitDonationSuccess,
  selectCurrentDonation,
  selectClientSecret,
  selectPaymentIntent,
  clearDonationErrors,
  clearDonationSuccess,
  resetDonationState,
  confirmPayment,
  selectConfirmingPayment,
  selectPaymentConfirmationError,
  selectPaymentConfirmationSuccess,
} from '../../store/slices/donationSlice';

import {
  selectDonationGoals,
  getPublicSettings
} from '../../store/slices/platformSettingsSlice';

// Stripe
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

import styles from '../../styles/DonationPage.module.css';

// ======================================================
// STRIPE INITIALIZATION
// ======================================================
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// ======================================================
// CARD ELEMENT STYLES
// ======================================================
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#32325d',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  }
};

// ======================================================
// SEPARATE COMPONENT FOR DONATION ITEM
// ======================================================
const DonationItem = ({ item, getPurposeLabel, getPurposeIcon, getStatusBadgeColor, getStatusColor }) => {
  const { translatedText: purposeLabel } = useTranslation(getPurposeLabel(item.purpose));
  const { translatedText: donorName } = useTranslation(item.donorName);
  
  return (
    <div className={styles.donationItem}>
      <div className={styles.donationItemBody}>
        <div className={styles.donationHeader}>
          <div className={styles.donorInfo}>
            <div className={styles.donorAvatar}>
              <span className={styles.donorInitial}>
                {item.isAnonymous ? 'A' : item.donorName?.charAt(0) || '?'}
              </span>
            </div>
            <div className={styles.donorInfoText}>
              <div className={styles.donorName}>
                {item.isAnonymous ? 'Anonymous Donor' : donorName || 'Unknown Donor'}
              </div>
              <div className={styles.donationPurpose}>
                {purposeLabel}
              </div>
            </div>
          </div>
          <div className={styles.donationAmountContainer}>
            <div className={styles.donationAmount}>
              ${item.amount?.toLocaleString() || 0}
            </div>
            <div 
              className={styles.donationStatus}
              style={{ 
                backgroundColor: getStatusBadgeColor(item.status), 
                color: getStatusColor(item.status) 
              }}
            >
              {item.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1) : 'Unknown'}
            </div>
          </div>
        </div>
        
        {item.message && (
          <p className={styles.donationMessage}>
            "{item.message}"
          </p>
        )}
        
        <div className={styles.donationFooter}>
          <div className={styles.donationDate}>
            <Clock size={14} color={colors.textTertiary} />
            <span>
              {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A'}
            </span>
          </div>
          {item.providerName && (
            <div className={styles.donationProvider}>
              <Building2 size={12} color={colors.textTertiary} />
              <span>
                For: {item.providerName}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ======================================================
// PAYMENT FORM COMPONENT (uses Stripe Elements)
// ======================================================
const PaymentForm = ({
  onSuccess,
  onError,
  isLoading,
  setIsLoading,
  donationData
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [cardError, setCardError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const submitError = useSelector(selectSubmitDonationError);
  const confirmingPayment = useSelector(selectConfirmingPayment);
  const paymentConfirmationError = useSelector(selectPaymentConfirmationError);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      setCardError('Stripe is not initialized. Please try again.');
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardError('Card element not found.');
      return;
    }

    setIsProcessing(true);
    setIsLoading(true);
    dispatch(clearDonationErrors());

    try {
      // Create the donation first
      const result = await dispatch(createDonationWithPayment(donationData)).unwrap();
      
      console.log('✅ Donation created:', result);
      
      if (result?.data?.clientSecret && result?.data?.donation?._id) {
        // Confirm the card payment with Stripe
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          result.data.clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: donationData.isAnonymous ? 'Anonymous' : donationData.donorName,
                email: donationData.donorEmail || undefined,
              }
            }
          }
        );

        if (error) {
          console.error('❌ Payment failed:', error);
          setCardError(error.message || 'Payment failed. Please try again.');
          onError(error.message);
          setIsProcessing(false);
          setIsLoading(false);
          return;
        }
        
        if (paymentIntent && paymentIntent.status === 'succeeded') {
          console.log('✅ Payment succeeded with Stripe, paymentIntentId:', paymentIntent.id);
          
          // Call confirmPayment with the payment intent ID
          try {
            const confirmResult = await dispatch(confirmPayment(paymentIntent.id)).unwrap();
            console.log('✅ Payment confirmation from backend:', confirmResult);
            
            // Payment confirmation success
            onSuccess(paymentIntent);
          } catch (confirmError) {
            console.error('❌ Payment confirmation failed:', confirmError);
            setCardError(confirmError || 'Failed to confirm payment. Please contact support.');
            onError(confirmError);
            setIsProcessing(false);
            setIsLoading(false);
          }
        } else if (paymentIntent && paymentIntent.status === 'requires_action') {
          // Handle 3D Secure if needed
          setCardError('Additional authentication required. Please follow the prompts.');
          onError('3D Secure authentication required');
          setIsProcessing(false);
          setIsLoading(false);
        } else {
          setCardError('Payment status: ' + (paymentIntent?.status || 'unknown'));
          onError('Payment not completed');
          setIsProcessing(false);
          setIsLoading(false);
        }
      } else {
        setCardError('Failed to initiate payment. Please try again.');
        onError('Failed to initiate payment');
        setIsProcessing(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('❌ Donation error:', error);
      setCardError(error.message || 'An error occurred. Please try again.');
      onError(error.message);
      setIsProcessing(false);
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Card Element */}
      <div className={styles.cardElementContainer}>
        <label className={styles.modalLabel}>Card Details</label>
        <div className={styles.cardElementWrapper}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        {cardError && (
          <div className={styles.cardError}>
            <X size={16} />
            <span>{cardError}</span>
          </div>
        )}
        {submitError && !cardError && (
          <div className={styles.cardError}>
            <X size={16} />
            <span>{submitError}</span>
          </div>
        )}
        {paymentConfirmationError && !cardError && !submitError && (
          <div className={styles.cardError}>
            <X size={16} />
            <span>{paymentConfirmationError}</span>
          </div>
        )}
      </div>

      <button
        type="submit"
        className={styles.submitButton}
        disabled={!stripe || isProcessing || isLoading || confirmingPayment}
      >
        {isProcessing || isLoading || confirmingPayment ? (
          <>
            <Loader2 size={20} className={styles.spinner} />
            <span>
              {confirmingPayment ? 'Confirming Payment...' : 'Processing...'}
            </span>
          </>
        ) : (
          <>
            <Heart size={20} />
            <span>Complete Donation</span>
          </>
        )}
      </button>
    </form>
  );
};

// ======================================================
// MAIN DONATION PAGE COMPONENT
// ======================================================
const DonationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [donationPurpose, setDonationPurpose] = useState('platform_support');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorMessage, setDonorMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  const { currentLanguage } = useLanguage();

  // ======================================================
  // REDUX SELECTORS
  // ======================================================
  const publicDonations = useSelector(selectPublicDonations);
  const featuredDonations = useSelector(selectFeaturedDonations);
  const donationGoals = useSelector(selectDonationGoals);
  const loading = useSelector(selectDonationsLoading);
  const submitting = useSelector(selectDonationSubmitting);
  const submitError = useSelector(selectSubmitDonationError);
  const submitSuccess = useSelector(selectSubmitDonationSuccess);
  const currentDonation = useSelector(selectCurrentDonation);
  const clientSecret = useSelector(selectClientSecret);
  const paymentIntent = useSelector(selectPaymentIntent);
  const confirmingPayment = useSelector(selectConfirmingPayment);
  const paymentConfirmationError = useSelector(selectPaymentConfirmationError);
  const paymentConfirmationSuccess = useSelector(selectPaymentConfirmationSuccess);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: donationsTitle } = useTranslation('Donations');
  const { translatedText: supportCommunity } = useTranslation('Support Our Community');
  const { translatedText: heroSubtitle } = useTranslation('Your donations help us connect Haitian communities worldwide');
  const { translatedText: overallProgress } = useTranslation('Overall Campaign Progress');
  const { translatedText: raised } = useTranslation('raised');
  const { translatedText: donationGoalsText } = useTranslation('Donation Goals');
  const { translatedText: donateNow } = useTranslation('Donate Now');
  const { translatedText: recentDonations } = useTranslation('Recent Donations');
  const { translatedText: allTab } = useTranslation('All');
  const { translatedText: platformTab } = useTranslation('Platform');
  const { translatedText: providersTab } = useTranslation('Providers');
  const { translatedText: noDonations } = useTranslation('No donations yet');
  const { translatedText: beFirst } = useTranslation('Be the first to support our community');
  const { translatedText: communityMessage } = useTranslation('Together we can build a stronger Haitian community');
  const { translatedText: manyHands } = useTranslation('"Men anpil, chay pa lou" - Many hands make light work');
  const { translatedText: purposeLabel } = useTranslation('Purpose');
  const { translatedText: selectPurpose } = useTranslation('Select a purpose for your donation');
  const { translatedText: amountLabel } = useTranslation('Amount');
  const { translatedText: customAmountLabel } = useTranslation('Custom Amount');
  const { translatedText: yourName } = useTranslation('Your Name');
  const { translatedText: namePlaceholder } = useTranslation('Enter your full name');
  const { translatedText: emailRequired } = useTranslation('Email (Required)');
  const { translatedText: emailPlaceholder } = useTranslation('Enter your email for receipt');
  const { translatedText: messageOptional } = useTranslation('Message (Optional)');
  const { translatedText: messagePlaceholder } = useTranslation('Leave a message of support');
  const { translatedText: anonymousDonation } = useTranslation('Donate anonymously');
  const { translatedText: completeDonation } = useTranslation('Complete Donation');
  const { translatedText: processing } = useTranslation('Processing...');
  const { translatedText: thankYou } = useTranslation('Thank You for Your Donation!');
  const { translatedText: donationReceived } = useTranslation('Your donation has been received and will make a difference in our community.');
  const { translatedText: close } = useTranslation('Close');

  // In DonationPage.jsx, add these missing translation imports at the top with other translations:

const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide');
const { translatedText: madeWithLove } = useTranslation('All rights reserved');
const { translatedText: quickLinks } = useTranslation('Quick Links');
const { translatedText: findProviders } = useTranslation('Find Providers');
const { translatedText: supportUs } = useTranslation('Support Us');
const { translatedText: helpAndSupport } = useTranslation('Help & Support');
const { translatedText: aboutUs } = useTranslation('About Us');
const { translatedText: forProviders } = useTranslation('For Providers');
const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
const { translatedText: providerLogin } = useTranslation('Provider Login');
const { translatedText: dashboard } = useTranslation('Dashboard');
const { translatedText: settings } = useTranslation('Settings');
const { translatedText: legal } = useTranslation('Legal');
const { translatedText: termsOfService } = useTranslation('Terms of Service');
const { translatedText: privacyPolicy } = useTranslation('Privacy Policy');
const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // ======================================================
  // FETCH DATA ON MOUNT
  // ======================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          dispatch(getPublicDonations({ limit: 20 })).unwrap(),
          dispatch(getFeaturedDonations()).unwrap(),
          dispatch(getPublicSettings()).unwrap()
        ]);
      } catch (error) {
        console.error('Failed to fetch donation data:', error);
      }
    };

    fetchData();

    return () => {
      dispatch(resetDonationState());
    };
  }, [dispatch]);

  // ======================================================
  // HANDLE SUCCESSFUL DONATION
  // ======================================================
  useEffect(() => {
    if (paymentConfirmationSuccess && currentDonation) {
      console.log('✅ Payment confirmed successfully!', currentDonation);
      setSuccess(true);
      setIsLoading(false);
      
      dispatch(getPublicDonations({ limit: 20 }));
      dispatch(getPublicSettings()); // Refresh to get updated donation goals
      
      const timer = setTimeout(() => {
        dispatch(clearDonationSuccess());
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [paymentConfirmationSuccess, currentDonation, dispatch]);

  // ======================================================
  // HANDLE ERRORS
  // ======================================================
  useEffect(() => {
    if (submitError || paymentConfirmationError) {
      console.error('❌ Donation error:', submitError || paymentConfirmationError);
      setIsLoading(false);
    }
  }, [submitError, paymentConfirmationError]);

  // ======================================================
  // HELPER FUNCTIONS
  // ======================================================
  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return colors.success;
      case 'succeeded': return colors.success;
      case 'pending': return colors.warning;
      case 'failed': return colors.error;
      case 'refunded': return colors.textTertiary;
      default: return colors.gray400;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'completed': return colors.success + '20';
      case 'succeeded': return colors.success + '20';
      case 'pending': return colors.warning + '20';
      case 'failed': return colors.error + '20';
      case 'refunded': return colors.gray200;
      default: return colors.gray200;
    }
  };

  // ======================================================
  // GET DONATION GOALS FROM PLATFORM SETTINGS
  // ======================================================
  const donationGoalsArray = Array.isArray(donationGoals) ? donationGoals : [];

  // Map donation goals from platform settings
  const donationGoalItems = donationGoalsArray.map(goal => ({
    id: goal._id || goal.id,
    title: goal.title || 'Donation Goal',
    description: goal.description || 'Support our community',
    targetAmount: Number(goal.targetAmount) || 0,
    currentAmount: Number(goal.currentAmount) || 0,
    icon: goal.icon || Code,
    color: colors.primary,
    isActive: goal.isActive,
    startDate: goal.startDate,
    endDate: goal.endDate
  }));

  // Check if there are any active donation campaigns
  const hasActiveCampaigns = donationGoalItems.length > 0;

  // ======================================================
  // CALCULATE OVERALL PROGRESS FROM GOAL CURRENT AMOUNTS
  // ======================================================
  
  // Use currentAmount from donation goals directly
  const totalRaised = donationGoalItems.reduce(
    (sum, goal) => sum + (Number(goal.currentAmount) || 0),
    0
  );

  const totalTarget = donationGoalItems.reduce(
    (sum, goal) => sum + (Number(goal.targetAmount) || 0),
    0
  );

  // Calculate percentage safely
  const overall_Progress = totalTarget > 0
    ? Math.min((totalRaised / totalTarget) * 100, 100)
    : 0;

  console.log('💰 Donation Progress (from goals):', {
    donationGoalItems: donationGoalItems.length,
    totalRaised,
    totalTarget,
    overall_Progress: Math.round(overall_Progress)
  });

  // ======================================================
  // DONATIONS LIST
  // ======================================================
  const donations = Array.isArray(publicDonations) ? publicDonations : [];
  
  const filteredDonations = donations.filter(donation => {
    if (selectedTab === 'all') return true;
    if (selectedTab === 'platform') return donation.purpose === 'platform_support' || donation.purpose === 'general';
    if (selectedTab === 'provider') return donation.providerId;
    return true;
  });

  const quickAmounts = [10, 25, 50, 100, 250, 500];

  const purposeOptions = [
    { id: 'platform_support', label: useTranslation('Platform Support').translatedText, icon: Phone },
    { id: 'community_development', label: useTranslation('Community Development').translatedText, icon: Users },
    { id: 'technical_support', label: useTranslation('Technical Support').translatedText, icon: Wrench },
    { id: 'general', label: useTranslation('General Donation').translatedText, icon: Heart }
  ];

  const getPurposeLabel = (purpose) => {
    const found = purposeOptions.find(p => p.id === purpose);
    return found ? found.label : purpose;
  };

  const getPurposeIcon = (purpose) => {
    const found = purposeOptions.find(p => p.id === purpose);
    return found ? found.icon : Heart;
  };

  // ======================================================
  // VALIDATE EMAIL
  // ======================================================
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ======================================================
  // HANDLE PAYMENT SUCCESS
  // ======================================================
  const handlePaymentSuccess = (paymentIntent) => {
    console.log('🎉 Payment success handler called:', paymentIntent);
  };

  // ======================================================
  // HANDLE PAYMENT ERROR
  // ======================================================
  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setPaymentError(error);
    setIsLoading(false);
  };

  const resetForm = () => {
    setSelectedAmount(25);
    setCustomAmount('');
    setDonationPurpose('platform_support');
    setDonorName('');
    setDonorEmail('');
    setDonorMessage('');
    setIsAnonymous(false);
    setSuccess(false);
    setPaymentError('');
    setShowDonateModal(false);
    dispatch(clearDonationErrors());
    dispatch(clearDonationSuccess());
  };

  // ======================================================
  // RENDER
  // ======================================================
  return (
    <div className={styles.donationsContainer}>
      {/* Header */}
      <div className={styles.donationsHeader}>
        <button 
          className={styles.backButton}
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={24} color={colors.textPrimary} />
        </button>
        <h2>{donationsTitle}</h2>
      </div>

      <div className={styles.container}>
        {/* Hero Section */}
        <div className={styles.heroSection}>
          <span className={styles.heroEmoji}>🤝</span>
          <h3 className={styles.heroTitle}>{supportCommunity}</h3>
          <p className={styles.heroSubtitle}>{heroSubtitle}</p>
        </div>

        {/* Overall Progress - Only show if there are campaigns */}
        {hasActiveCampaigns && (
          <div className={styles.overallProgressContainer}>
            <div className={styles.overallProgressBody}>
              <div className={styles.progressHeader}>
                <span className={styles.progressLabel}>{overallProgress}</span>
                <span className={styles.progressPercentage} style={{ color: colors.primary }}>
                  {Math.round(overall_Progress)}%
                </span>
              </div>
              <div className={styles.progressBar}>
                <div 
                  className={styles.progressFill}
                  style={{ 
                    width: `${Math.min(overall_Progress, 100)}%`,
                    backgroundColor: colors.primary
                  }}
                />
              </div>
              <div className={styles.progressStats}>
                <span className={styles.progressRaised}>${totalRaised.toLocaleString()} {raised}</span>
                <span className={styles.progressTarget}>of ${totalTarget.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* Donation Goals - ONLY from platform settings */}
        <div className={styles.section}>
          <h5 className={styles.sectionTitle}>{donationGoalsText}</h5>
          {donationGoalItems.length > 0 ? (
            donationGoalItems.map((goal) => {
              const Icon = goal.icon;
              // Calculate progress based on goal's currentAmount vs targetAmount
              const progress = goal.targetAmount > 0
                ? Math.min((goal.currentAmount / goal.targetAmount) * 100, 100)
                : 0;
              return (
                <div key={goal.id} className={styles.goalCard}>
                  <div className={styles.goalCardBody}>
                    <div className={styles.goalHeader}>
                      <div className={styles.goalIcon} style={{ backgroundColor: (goal.color || colors.primary) + '15' }}>
                        <Icon size={24} color={goal.color || colors.primary} />
                      </div>
                      <div className={styles.goalInfo}>
                        <h6 className={styles.goalTitle}>{goal.title}</h6>
                        <p className={styles.goalDescription}>{goal.description}</p>
                        {goal.endDate && (
                          <p className={styles.goalDate}>
                            <Calendar size={14} color={colors.textTertiary} />
                            <span>Target: {new Date(goal.endDate).toLocaleDateString()}</span>
                          </p>
                        )}
                        {goal.isActive === false && (
                          <span className={styles.goalInactive}>Completed</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.goalProgress}>
                      <div className={styles.goalProgressBar}>
                        <div 
                          className={styles.goalProgressFill}
                          style={{ 
                            width: `${Math.min(progress, 100)}%`, 
                            backgroundColor: goal.color || colors.primary
                          }}
                        />
                      </div>
                      <div className={styles.goalAmounts}>
                        <span className={styles.goalRaised}>
                          ${goal.currentAmount.toLocaleString()} {raised}
                        </span>
                        <span className={styles.goalTarget}>
                          Goal: ${goal.targetAmount.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className={styles.emptyContainer}>
              <Target size={48} color={colors.gray300} />
              <h6 className={styles.emptyTitle}>No Active Campaigns</h6>
              <p className={styles.emptySubtitle}>No donation campaigns have been set up yet. Check back soon!</p>
            </div>
          )}
        </div>

        {/* Donate Button */}
        <button
          className={styles.donateButton}
          onClick={() => setShowDonateModal(true)}
        >
          <Heart size={24} color={colors.white} />
          <span className={styles.donateButtonText}>{donateNow}</span>
        </button>

        {/* Recent Donations - Only show if there are campaigns */}
        {hasActiveCampaigns && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h5 className={styles.sectionTitle}>{recentDonations}</h5>
              <div className={styles.tabContainer}>
                {[
                  { id: 'all', label: allTab },
                  { id: 'platform', label: platformTab },
                  { id: 'provider', label: providersTab }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className={`${styles.tab} ${selectedTab === tab.id ? styles.tabActive : styles.tabInactive}`}
                    onClick={() => setSelectedTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className={styles.loadingContainer}>
                <Loader2 size={32} className={styles.spinner} />
                <p>Loading donations...</p>
              </div>
            ) : filteredDonations.length > 0 ? (
              filteredDonations.map((item) => (
                <DonationItem 
                  key={item._id || item.id}
                  item={item}
                  getPurposeLabel={getPurposeLabel}
                  getPurposeIcon={getPurposeIcon}
                  getStatusBadgeColor={getStatusBadgeColor}
                  getStatusColor={getStatusColor}
                />
              ))
            ) : (
              <div className={styles.emptyContainer}>
                <Heart size={48} color={colors.gray300} />
                <h6 className={styles.emptyTitle}>{noDonations}</h6>
                <p className={styles.emptySubtitle}>{beFirst}</p>
              </div>
            )}
          </div>
        )}

        {/* Community Message */}
        <div className={styles.communityMessage} style={{ 
          backgroundColor: colors.secondary + '10', 
          borderColor: colors.secondary + '20' 
        }}>
          <p className={styles.communityQuote}>{manyHands}</p>
          <p className={styles.communityText}>{communityMessage}</p>
          <div className={styles.flagStripes}>
            <div className={`${styles.stripe} ${styles.stripeBlue}`} />
            <div className={`${styles.stripe} ${styles.stripeRed}`} />
          </div>
        </div>
      </div>

      {/* Donate Modal */}
      {showDonateModal && (
        <div className={styles.modalOverlay} onClick={() => !isLoading && resetForm()}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            {!success ? (
              <>
                <div className={styles.modalHeader}>
                  <h2 className={styles.modalTitle}>{donateNow}</h2>
                  <button 
                    className={styles.modalClose}
                    onClick={() => !isLoading && resetForm()}
                    disabled={isLoading || confirmingPayment}
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className={styles.modalBody}>
                  {paymentError && (
                    <div className={styles.errorMessage}>
                      <X size={20} />
                      <span>{paymentError}</span>
                    </div>
                  )}

                  {/* Purpose Selection */}
                  <div className={styles.modalField}>
                    <label className={styles.modalLabel}>{purposeLabel}</label>
                    <div className={styles.purposeOptions}>
                      {purposeOptions.map((purpose) => {
                        const Icon = purpose.icon;
                        return (
                          <button
                            key={purpose.id}
                            className={`${styles.purposeOption} ${donationPurpose === purpose.id ? styles.active : ''}`}
                            onClick={() => setDonationPurpose(purpose.id)}
                          >
                            <Icon size={16} />
                            <span>{purpose.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Amount Selection */}
                  <div className={styles.modalField}>
                    <label className={styles.modalLabel}>{amountLabel}</label>
                    <div className={styles.amountOptions}>
                      {quickAmounts.map((amount) => (
                        <button
                          key={amount}
                          className={`${styles.amountOption} ${selectedAmount === amount && !customAmount ? styles.active : ''}`}
                          onClick={() => {
                            setSelectedAmount(amount);
                            setCustomAmount('');
                          }}
                        >
                          ${amount}
                        </button>
                      ))}
                    </div>
                    <div className={styles.customAmountInput}>
                      <DollarSign size={20} className={styles.customAmountIcon} />
                      <input
                        type="number"
                        placeholder={customAmountLabel}
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setSelectedAmount(0);
                        }}
                        min="1"
                      />
                    </div>
                  </div>

                  {/* Donor Info */}
                  <div className={styles.modalField}>
                    <label className={styles.modalLabel}>{yourName}</label>
                    <input
                      type="text"
                      className={styles.modalInput}
                      placeholder={namePlaceholder}
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      disabled={isAnonymous}
                    />
                  </div>

                  {/* Email Field - Required */}
                  <div className={styles.modalField}>
                    <label className={styles.modalLabel}>
                      {emailRequired}
                      <span style={{ color: colors.error, marginLeft: '4px' }}>*</span>
                    </label>
                    <input
                      type="email"
                      className={styles.modalInput}
                      placeholder={emailPlaceholder}
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className={styles.modalField}>
                    <label className={styles.modalLabel}>{messageOptional}</label>
                    <textarea
                      className={styles.modalTextarea}
                      placeholder={messagePlaceholder}
                      value={donorMessage}
                      onChange={(e) => setDonorMessage(e.target.value)}
                      rows={3}
                    />
                  </div>

                  {/* Anonymous Toggle */}
                  <div className={styles.anonymousToggle}>
                    <button
                      className={styles.anonymousToggleBtn}
                      onClick={() => setIsAnonymous(!isAnonymous)}
                    >
                      <div className={`${styles.anonymousToggleCheckbox} ${isAnonymous ? styles.checked : ''}`}>
                        {isAnonymous && <Check size={16} color={colors.white} />}
                      </div>
                      <span>{anonymousDonation}</span>
                    </button>
                  </div>

                  {/* Payment Form - Card Only */}
                  <Elements stripe={stripePromise}>
                    <PaymentForm
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      isLoading={isLoading}
                      setIsLoading={setIsLoading}
                      donationData={{
                        amount: customAmount ? parseFloat(customAmount) : selectedAmount,
                        purpose: donationPurpose,
                        donorName: isAnonymous ? 'Anonymous' : donorName.trim(),
                        donorEmail: donorEmail.trim(),
                        message: donorMessage.trim() || undefined,
                        isAnonymous: isAnonymous,
                        currency: 'USD',
                        metadata: {
                          purpose: donationPurpose,
                          source: 'donation_page'
                        }
                      }}
                    />
                  </Elements>
                </div>
              </>
            ) : (
              <>
                <div className={styles.modalHeader}>
                  <h2 className={styles.modalTitle}>{thankYou}</h2>
                  <button 
                    className={styles.modalClose}
                    onClick={resetForm} 
                  >
                    <X size={24} />
                  </button>
                </div>
                <div className={styles.modalBody}>
                  <div className={styles.successContainer}>
                    <div className={styles.successIcon}>
                      <Check size={48} color={colors.white} />
                    </div>
                    <p className={styles.successText}>{donationReceived}</p>
                    <button
                      className={styles.successButton}
                      onClick={resetForm}
                    >
                      {close}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}


     <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicy}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          <div className={styles.footerDividerFull}></div>
          
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © {new Date().getFullYear()} {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default DonationPage;